import { toCalendarDate, toTime as toHourTime } from "@internationalized/date";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { useFormContext } from "react-hook-form";
import {
  Box,
  DropsDatePicker,
  Field,
  FieldErrorText,
  Text,
  TimePicker,
} from "shared/components/Spor";
import { VisibilityForm } from "shared/form/visibilitySchema";
import { EndsWith } from "shared/utils/objectUtils";

type VisibilityInputProps = {
  isRelatedToIncident: boolean;
  visibilityField: EndsWith<"visibility">;
  subField: "fromTime" | "toTime";
};

export const VisibilityInput = ({
  isRelatedToIncident,
  visibilityField: _visibilityField,
  subField,
}: VisibilityInputProps) => {
  /**
   * Zod doesnt understand non-const pathes that well, so we
   * trick it to think visibilityField is a const "visibility".
   */
  const visibilityField = _visibilityField as "visibility";
  type FormWithVisibility = { [visibilityField]: VisibilityForm };

  const { watch, setValue, formState } = useFormContext<FormWithVisibility>();

  const visibility = watch(visibilityField);
  if (visibility === undefined) {
    log(
      LogLevel.error,
      "VisibilityInput",
      `FormField (${visibilityField}) does not have a defaultValue. This component is most likely set up wrong`,
    );
    return null;
  }

  if (visibility.type === "CONSTANT") {
    log(
      LogLevel.error,
      "VisibilityInput",
      `VisibilityInput should be used for TIMED or OPEN_ENDED visibility`,
    );
    return null;
  }

  if (subField === "toTime" && visibility.type === "OPEN_ENDED") {
    log(
      LogLevel.error,
      "VisibilityInput",
      `toTime and visibility.type OPEN_ENDED is an illegal combination for VisibilityInput`,
    );
    return null;
  }

  if (isRelatedToIncident) {
    return (
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        rowGap="6px"
        columnGap="12px"
        width="100%"
        paddingY="12px"
      >
        <Text variant="xs" gridColumn="span 2">
          Synlighet:
        </Text>
        {isRelatedToIncident && (
          <Text variant="xs" fontWeight="bold" gridColumn="span 2">
            Følger synlighet til tilhørende infrastrukturmelding
          </Text>
        )}
      </Box>
    );
  }

  const fieldError = formState.errors.root;
  const dateError =
    !!fieldError &&
    visibility.type === "TIMED" &&
    visibility.fromTime.day > visibility.toTime.day;

  const visibilityTime =
    visibility.type === "TIMED" ? visibility[subField] : visibility.fromTime;
  const field = `${visibilityField}.${subField}` as const;

  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr 1fr"
      rowGap="6px"
      columnGap="12px"
      width="100%"
      paddingY="12px"
    >
      <Text variant="xs" gridColumn="span 2">
        Synlig {subField === "fromTime" ? "fra" : "inntil"}:
      </Text>
      {isRelatedToIncident && (
        <Text variant="xs" fontWeight="bold" gridColumn="span 2">
          Følger synlighet til tilhørende infrastrukturmelding
        </Text>
      )}
      <Field invalid={dateError}>
        <DropsDatePicker
          width="100%"
          variant="core"
          label="Dato"
          value={toCalendarDate(visibilityTime)}
          onValueChange={(date) => {
            if (date === null) return;
            setValue(field, visibilityTime.set({ ...date }));
          }}
          withPortal={false}
        />
        <FieldErrorText>{fieldError?.message}</FieldErrorText>
      </Field>
      <Field invalid={!!fieldError && !dateError}>
        <TimePicker
          width="100%"
          label="Tidspunkt"
          value={toHourTime(visibilityTime)}
          minuteInterval={5}
          onValueChange={(time) => {
            if (time === null) return;
            setValue(field, visibilityTime.set({ ...time }));
          }}
        />
        <FieldErrorText>{fieldError?.message}</FieldErrorText>
      </Field>
    </Box>
  );
};
